import type { WorkItemSummary } from '../types/workflow.js';
import { describeSources, type DescribedSource } from './sources.js';
import { isPracticeItem } from './workItems.js';

/**
 * Whether the reviewer can check this item's claims against the curriculum.
 *
 * AD-27: every practice item in the production queue arrived with `sourceRefs: []` — 43 of 43 —
 * so the Academic Director reviewed exam items with no way to verify a single claim. AD-30 decision
 * 2 chose a **soft gate** for this: a prominent banner and an acknowledgement on approve, plus a
 * queue readiness badge. Scott called provenance valuable but explicitly **not** a blocker, so this
 * must never become a hard gate — a hard gate on a cohort that cannot yet satisfy it is a work
 * stoppage.
 *
 * - `none`       the item cites nothing at all
 * - `unresolved` it cites something, but nothing the reviewer can open
 * - `ready`      at least one cited document is openable
 */
export type EvidenceReadiness = 'none' | 'unresolved' | 'ready';

/** The reviewer cross-references printed curriculum pages; only PDFs are openable evidence. */
const OPENABLE_PATH = /\.pdf$/i;

/**
 * The reviewer-visible reference list. `PracticeItemMetadata` renders exactly this, so the gate and
 * the panel cannot disagree: an item whose panel says "No PDF references attached" must be an item
 * the gate fires on.
 */
export function reviewableSources(item: Pick<WorkItemSummary, 'sourceRefs'>): DescribedSource[] {
  return describeSources(item.sourceRefs ?? []).filter((source) => OPENABLE_PATH.test(source.path));
}

export function evidenceReadiness(item: Pick<WorkItemSummary, 'sourceRefs'>): EvidenceReadiness {
  if ((item.sourceRefs ?? []).length === 0) {
    return 'none';
  }
  // `missing` is the only availability that PROVES the document is not there (absent, or a filename
  // that matched two documents). `unchecked` means the deployment could not answer — a throttled
  // HEAD, or a redirect deployment where `checkSourceAvailability` returns `unchecked` for every
  // path it is given (`source-documents.service.ts`). It is not evidence that the source exists, and
  // treating it as ready therefore claims slightly more than we know — deliberately, because a
  // redirect deployment stamps EVERY ref `unchecked`, and a gate that fires on every item is as bad
  // as no gate. Absent availability is how pre-AD-27 and non-strict imports arrive; same treatment.
  const openable = reviewableSources(item).some((source) => source.availability !== 'missing');
  return openable ? 'ready' : 'unresolved';
}

/**
 * Which items the gate and the readiness badge apply to: **practice items only**.
 *
 * AD-30 decision 2 came out of the practice-item review session, and the scope is not cosmetic —
 * `reviewableSources` counts PDFs only, which is right for an exam item whose evidence is a printed
 * curriculum page, and wrong for a structural item that legitimately cites a JSON artefact. Applying
 * one rule to every row produced a row badged "no evidence" beside an Evidence column showing a card
 * (gpt-5.6-sol, 2026-07-30). Widening this to lesson drafts is a one-line change plus copy that fits
 * their evidence shape; it is an owner decision, not an oversight.
 */
export function evidenceGateApplies(item: WorkItemSummary): boolean {
  return isPracticeItem(item);
}

/**
 * The fixed acknowledgement recorded when an item is approved with no checkable evidence.
 *
 * Owner decision (AD-33 #3): **checkbox only** — no note field, no required reason, nothing stored
 * beyond the fact of the acknowledgement. It rides the `notes` argument that
 * `approveItem(id, notes?)` already accepts, which `queue.service.ts` writes to both the review
 * event (`author-review-events`) and the audit row (`author-audit`, `details.notes`). No API change.
 */
export const EVIDENCE_ACK_NOTE =
  'Approved with no checkable curriculum evidence — reviewer acknowledged the gap.';
